import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { green, red, amber, blue } from '@mui/material/colors';
import { ApiInstance } from '../../../api/api';
import { DeviceSwitchPortsDataType, VlanDataType } from '../../../types/buildingTypes';
import {
  PortActionDialogState,
  JuniperPortFlipData,
  PerformPortFlipRequestType,
  PerformMerakiPortFlipRequestType,
  PortFlipData,
  MerakiPortFlipData,
  PortActionSuccess,
  PortActionFailure,
  PortActionBatchResponse,
} from '../../../types/types';
import IconButton from '@mui/material/IconButton';
import PortFlipForm from './PortFlipForm';
import DialogContentLoading from '../common/DialogContentLoading';
import DialogContentSuccess from '../common/DialogContentSuccess';
import DialogContentError from '../common/DialogContentError';
import dialogStyles from '../common/dialogStyles';
import { PortActionError, portActionErrorDesc } from '../../../constants/errorCodes';
import { AxiosResponse } from 'axios';
import DialogContentBatchResult from '../common/DialogContentBatchResult';
import { parseApiError } from '../../../utils/apiErrorHandler';

interface PortFlipActionProps {
  open: boolean;
  onClose: () => void;
  unlimitedPortFlip: boolean;
  ipAddress: string | undefined;
  deviceType: string | null;
  region: string | null;
  buildingCode: string | null;
  unit: string | undefined;
  unitName: string | null;
  selectedPorts: DeviceSwitchPortsDataType[];
  availableVlans: VlanDataType | null;
  isJuniper: boolean;
  isMeraki: boolean;
  isApiMode: boolean;
  isDescriptionEditable?: boolean;
  isDistro?: boolean;
}

function useSelectedItem(items: VlanDataType | null, isDistro: boolean) {
  const [selectedVlan, setSelectedVlan] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [touched, setTouched] = useState<{
    selectedVlan: boolean;
    newDescription: boolean;
  }>({
    selectedVlan: false,
    newDescription: false,
  });
  const isVlanValid: boolean = selectedVlan.trim().length > 0;
  const allowedInputCharacters: RegExp = isDistro ? /^[a-zA-Z0-9 _/:()|-]*$/ : /^[a-zA-Z0-9 _-]*$/;

  const isDescriptionValid: boolean =
    description.trim().length > 0 && allowedInputCharacters.test(description);
  const markVlanSelectTouched = useCallback(() => setTouched((t) => ({ ...t, vlan: true })), []);
  const markDescriptionTouched = useCallback(
    () => setTouched((t) => ({ ...t, description: true })),
    []
  );

  return {
    selectedVlan,
    setSelectedVlan,
    description,
    setDescription,
    isVlanValid,
    allowedInputCharacters,
    isDescriptionValid,
    touched,
    markVlanSelectTouched,
    markDescriptionTouched,
    setTouched,
  };
}

const PortFlipAction: React.FC<PortFlipActionProps> = ({
  open,
  onClose,
  unlimitedPortFlip,
  ipAddress,
  deviceType,
  region,
  buildingCode,
  unit,
  unitName,
  selectedPorts,
  availableVlans,
  isJuniper,
  isMeraki,
  isApiMode,
  isDescriptionEditable = true,
  isDistro = false,
}) => {
  const {
    selectedVlan,
    setSelectedVlan,
    description,
    setDescription,
    isVlanValid,
    allowedInputCharacters,
    isDescriptionValid,
    touched,
    markVlanSelectTouched,
    markDescriptionTouched,
    setTouched,
  } = useSelectedItem(availableVlans, isDistro);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const isConfirmDisabled = !isVlanValid || !isDescriptionValid;
  const selectRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setSelectedVlan('');
    setDescription('');
    setTouched({ selectedVlan: false, newDescription: false });
    setSubmitAttempted(false);
  };

  const DEFAULT_LOADING_TEXT = 'Please wait while we process your request...';
  const DEFAULT_ERROR_TEXT = 'Something went wrong. Please try again later.';

  const titleMap: Record<PortActionDialogState, string> = {
    default: `Are you sure you want to flip the following port${selectedPorts.length !== 1 ? 's' : ''}?`,
    loading: 'Flip action',
    success: 'Flip action',
    partial: 'Flip action',
    error: 'Flip action',
  };

  const backgroundColorMap: Record<PortActionDialogState, string> = {
    default: amber[800],
    loading: blue[600],
    success: green[500],
    partial: amber[800],
    error: red[500],
  };

  const [dialogState, setDialogState] = useState<PortActionDialogState>('default');
  const [loadingText, setLoadingText] = useState(DEFAULT_LOADING_TEXT);
  const [errorText, setErrorText] = useState(DEFAULT_ERROR_TEXT);
  const [reloadOnClose, setReloadOnClose] = useState<Boolean>(false);
  const [processedPorts, setProcessedPorts] = useState<PortActionSuccess[]>([]);
  const [failedPorts, setFailedPorts] = useState<PortActionFailure[]>([]);
  const showWaitBlock = isJuniper || isMeraki;

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setDialogState('default');
        setLoadingText(DEFAULT_LOADING_TEXT);
        setErrorText(DEFAULT_ERROR_TEXT);
        setReloadOnClose(false);
        setProcessedPorts([]);
        setFailedPorts([]);
      }, 100);
    }
  }, [open]);

  const resolveBatchDialogState = (
    succeeded: PortActionSuccess[],
    failed: PortActionFailure[]
  ): PortActionDialogState => {
    const hasSuccesses = succeeded.length > 0;
    const hasFailures = failed.length > 0;

    if (hasSuccesses && !hasFailures) return 'success';
    if (!hasSuccesses && hasFailures) return 'error';
    if (hasSuccesses && hasFailures) return 'partial';
    return 'error';
  };

  const shouldReloadOnClose = (state: PortActionDialogState): boolean =>
    state === 'success' || state === 'partial';

  const withBatchDialogStateHandling = async (
    fn: () => Promise<PortActionBatchResponse>
  ): Promise<void> => {
    setDialogState('loading');
    setLoadingText('Please wait while we process your request...');

    try {
      const { succeeded, failed } = await fn();

      setProcessedPorts(succeeded);
      setFailedPorts(failed);

      const state = resolveBatchDialogState(succeeded, failed);
      setDialogState(state);
      setReloadOnClose(shouldReloadOnClose(state));
    } catch (error: any) {
      setDialogState('error');
      setReloadOnClose(false);
      setErrorText(parseApiError(error));
    }
  };

  const withDialogStateHandling = async (fn: () => Promise<AxiosResponse<any>>): Promise<void> => {
    setDialogState('loading');
    setLoadingText('Please wait while we process your request...');

    try {
      await fn();
      setDialogState('success');
      setReloadOnClose(true);
    } catch (error: any) {
      setDialogState('error');
      setReloadOnClose(false);

      if (error.response?.data?.detail?.error_code) {
        const error_code: PortActionError = error.response.data.detail.error_code;
        setErrorText(portActionErrorDesc[error_code]);
      } else {
        setErrorText(parseApiError(error));
      }
    }
  };

  const performClassicPortFlip = async (
    isJuniper: boolean,
    new_vlan: string,
    new_description: string
  ) => {
    let ports: PortFlipData[] | JuniperPortFlipData[];

    if (isJuniper) {
      ports = selectedPorts.map(({ port, vlan, profile, description }) => ({
        port,
        vlan,
        profile,
        new_profile: new_vlan,
        description,
        new_description: isDescriptionEditable ? new_description : description,
      }));
    } else {
      ports = selectedPorts.map(({ port, status, vlan, description }) => ({
        port,
        status,
        vlan,
        new_vlan,
        description,
        new_description: isDescriptionEditable ? new_description : description,
      }));
    }

    const payload: PerformPortFlipRequestType = {
      ip_address: ipAddress,
      device_type: deviceType,
      region: region,
      building_code: buildingCode,
      unit: unit,
      unit_name: unitName,
      api_mode: isApiMode,
      ports: ports,
    };

    const getApiCall = () => {
      if (isDistro) {
        return () => ApiInstance.performPortFlipMacMini(payload);
      }
      if (unlimitedPortFlip) {
        return () => ApiInstance.performPortFlip(payload);
      }
      return () => ApiInstance.performLimitedPortFlip(payload);
    };

    await withDialogStateHandling(getApiCall());
  };

  const performMerakiPortFlip = async (new_vlan: string, new_description: string) => {
    const ports: MerakiPortFlipData[] = selectedPorts.map(
      ({
        port_id,
        port,
        status,
        vlan,
        switch_serial,
        switch_name,
        switch_mac_address,
        description,
      }) => ({
        port_id,
        port,
        status,
        vlan,
        switch_serial,
        switch_name,
        switch_mac_address,
        description,
        new_vlan,
        new_description,
      })
    );

    const payload: PerformMerakiPortFlipRequestType = {
      ip_address: ipAddress,
      device_type: deviceType,
      region: region,
      building_code: buildingCode,
      unit: unit,
      unit_name: unitName,
      api_mode: false,
      ports: ports,
    };
    const apiCall = unlimitedPortFlip
      ? () => ApiInstance.performMerakiPortFlip(payload)
      : () => ApiInstance.performLimitedMerakiPortFlip(payload);

    await withBatchDialogStateHandling(apiCall);
  };

  const handleConfirm = async () => {
    setSubmitAttempted(true);

    if (!isConfirmDisabled) {
      const new_vlan = selectedVlan;
      const new_description = description;

      if (isMeraki) {
        await performMerakiPortFlip(new_vlan, new_description);
      } else {
        await performClassicPortFlip(isApiMode && isJuniper, new_vlan, new_description);
      }
    } else {
      if (!isVlanValid && selectRef.current) {
        selectRef.current.focus();
      } else if (!isDescriptionValid && descriptionRef.current) {
        descriptionRef.current.focus();
      }
    }
  };

  const handleClose = () => {
    if (reloadOnClose) {
      resetForm();
      window.location.reload();
    } else {
      resetForm();
      onClose();
    }
  };

  const getDialogTitle = () => titleMap[dialogState];
  const getTitleBackgroundColor = () => backgroundColorMap[dialogState];

  const getDialogContent = () => {
    switch (dialogState) {
      case 'loading':
        return <DialogContentLoading loadingText={loadingText} />;
      case 'success':
        return <DialogContentSuccess waitBlock={showWaitBlock} />;
      case 'partial':
        return (
          <DialogContentBatchResult
            waitBlock={showWaitBlock}
            succeeded={processedPorts}
            failed={failedPorts}
          />
        );
      case 'error':
        return <DialogContentError errorText={errorText} />;
      default:
        return (
          <>
            <PortFlipForm
              selectedPorts={selectedPorts}
              selectedVlan={selectedVlan}
              setSelectedVlan={setSelectedVlan}
              description={description}
              setDescription={setDescription}
              availableVlans={availableVlans}
              isVlanValid={isVlanValid}
              allowedInputCharacters={allowedInputCharacters}
              isDescriptionValid={isDescriptionValid}
              touched={touched}
              markVlanSelectTouched={markVlanSelectTouched}
              markDescriptionTouched={markDescriptionTouched}
              submitAttempted={submitAttempted}
              isJuniper={isJuniper}
              isDescriptionEditable={isDescriptionEditable}
              isDistro={isDistro}
            />
          </>
        );
    }
  };

  const showCloseIcon = dialogState !== 'loading';
  const showDialogActions = dialogState === 'default';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={dialogStyles.dialog}
      sx={dialogStyles.dialogPaper}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-content"
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          ...dialogStyles.dialogTitle,
          backgroundColor: getTitleBackgroundColor(),
        }}
      >
        {getDialogTitle()}
        {showCloseIcon && (
          <IconButton aria-label="close" onClick={handleClose} sx={dialogStyles.dialogCloseButton}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent id="dialog-content" sx={dialogStyles.dialogContent}>
        {getDialogContent()}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="contained"
            sx={dialogStyles.cancelButtonStyles}
          >
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Confirm
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PortFlipAction;
